/* istanbul ignore file */
import { useDateService, DATE_FORMAT } from 'framework/utils';
import {
  PROGRESS_STATUSES,
  PROGRESS_FIELDS_ORDER,
  PROGRESS_FIELDS_LABELS,
  PROGRESS_FIELDS_KEYS,
} from 'domain/sales/projectsDashboard/useProjectsDashboardService';
import { ProgressField as BaseProgressField, DateField, TextField, EnumField } from 'framework/ui/field';
import { Box, Typography } from '@mui/material';
import { PositionEnumField, SIMPLE_QUALIFICATION } from 'domain/shared/presale';
import { UiThemeOverrider } from 'framework/uiTheme';
import { getProgressFieldIcon } from './progressFieldUtils';

const ProgressFieldData = ({
  status = '',
  duration = 0,
  planDate = null,
  factDate = null,
  field,
  index,
}: Dictionary<any>) => {
  const { formatDate } = useDateService();

  const date =
    status === PROGRESS_STATUSES.COMPLETED || status === PROGRESS_STATUSES.COMPLETED_WITH_DELAY ? factDate : planDate;

  return (
    <>
      <Typography variant="h3">{PROGRESS_FIELDS_LABELS[field as PROGRESS_FIELDS_KEYS]}</Typography>
      <Typography variant="body1">T0 {index ? `+ ${duration || 0} DAY${duration === 1 ? '' : 'S'}` : ''}</Typography>
      <Typography variant="body1">
        {`${
          status === PROGRESS_STATUSES.IN_PROGRESS || status === PROGRESS_STATUSES.IN_PROGRESS_WITH_DELAY ? 'Plan ' : ''
        }${date ? formatDate(date, DATE_FORMAT.FULL_DATE) : '-'}`}
      </Typography>
    </>
  );
};

const ProgressFieldAdditionalData = ({
  status = '',
  duration = 0,
  planDate = null,
  factDate = null,
  field,
  index,
  additionalInfo = {},
}: Dictionary<any>) => {
  return (
    <>
      <DateField source="planDate" value={planDate} label="Plan date" />
      <DateField source="factDate" value={factDate} label="Actual date" />

      {additionalInfo?.expectedProjectStart && (
        <DateField
          source="expectedProjectStart"
          value={additionalInfo?.expectedProjectStart}
          label="Expected start date"
          className="mt-2"
        />
      )}

      {additionalInfo?.requiredResources && (
        <TextField
          label="Team structure"
          source="requiredResources"
          className="mt-2"
          value={
            <UiThemeOverrider
              overriddenLayouts={{
                BaseFieldLayout: ({ value }) => <span>{value}</span>,
              }}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {additionalInfo.requiredResources?.map(
                  ({ positionId, requiredFte, qualification }: Dictionary<string>) => (
                    <li key={`${field}-${index}-required-resources`}>
                      <PositionEnumField source="positionId" value={positionId} />- {requiredFte}%,{' '}
                      <EnumField
                        options={SIMPLE_QUALIFICATION}
                        value={qualification}
                        source="qualification"
                        label="Qualification"
                      />
                    </li>
                  ),
                )}
              </Box>
            </UiThemeOverrider>
          }
        />
      )}
    </>
  );
};

const ProgressField = ({ source }: Dictionary<any>) => {
  return (
    <Box
      sx={{
        mb: '-8px',
        p: 0.25,
        overflow: 'auto',
        contain: 'inline-size',
        '& .project-dashboard-timeline__item .project-dashboard-timeline__item-text': {
          minWidth: '160px',
          height: '64px',
          flexShrink: 0,
          flexGrow: 0,
        },
        '& .project-dashboard-timeline': {
          pb: '8px',
        },
      }}
    >
      <BaseProgressField
        source={source}
        steps={PROGRESS_FIELDS_ORDER}
        additionalData={<ProgressFieldAdditionalData />}
        getIcon={getProgressFieldIcon}
      >
        <ProgressFieldData />
      </BaseProgressField>
    </Box>
  );
};

export default ProgressField;
